import { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';

// Warehouse mobile — Packed Box Images (search & view). Opened from the
// third tile on WarehouseHome.jsx. Read-only counterpart of PoBoxImages.jsx
// (the capture/upload tile): the team types a PO number and sees every box
// photo for it. Uses the same /po-box-images-search endpoint as the desktop
// PO & SO Lifecycle page (pages/PackedBoxImages.jsx), so results match it
// exactly — including photos filed under the SO voucher number or a
// near-identical PO (>= 95% match). Tapping a photo opens a full-screen
// viewer with swipe between photos and pinch / double-tap zoom.

const C = {
    white: '#ffffff',
    ink:   '#111111',
    red:   '#D0202A',
    gray:  '#6b6b6b',
    line:  '#e6e6e6',
    panel: '#f7f7f7',
};

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function WhPackedBoxImages({ onBack }) {
    const [query,    setQuery]    = useState('');
    const [searched, setSearched] = useState('');   // the term the current results are for
    const [images,   setImages]   = useState([]);
    const [soMatches, setSoMatches] = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState('');
    const [viewIdx,  setViewIdx]  = useState(null); // index into images, null = viewer closed

    async function runSearch(e) {
        e?.preventDefault();
        const q = query.trim();
        if (!q) return;
        document.activeElement?.blur(); // drop the on-screen keyboard
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/po-box-images-search?q=${encodeURIComponent(q)}`);
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Search failed');
            setImages((data.images || []).filter(img => img.view_url));
            setSoMatches(data.so_matches || []);
            setSearched(q);
        } catch (err) {
            setError(err.message || 'Search failed');
        }
        setLoading(false);
    }

    const S = {
        page:   { minHeight:'100vh', background:C.white, color:C.ink, fontFamily:'-apple-system, BlinkMacSystemFont, sans-serif', maxWidth:'430px', margin:'0 auto' },
        header: { background:C.ink, padding:'16px 18px', position:'sticky', top:0, zIndex:100 },
        body:   { padding:'16px' },
        label:  { fontSize:'11px', color:C.gray, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' },
        input:  { flex:1, minWidth:0, padding:'14px 12px', background:'#f9f9f9', border:`1px solid ${C.line}`, borderRadius:'8px', color:'#111', fontSize:'17px', outline:'none', boxSizing:'border-box', fontFamily:'monospace', letterSpacing:'0.5px' },
        btn:    { padding:'0 18px', background:C.ink, color:'#fff', border:'none', borderRadius:8, fontSize:15, fontWeight:700, cursor:'pointer', flexShrink:0 },
        grid:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
        tile:   { background:C.white, border:`1px solid ${C.line}`, borderRadius:10, overflow:'hidden', padding:0, textAlign:'left', cursor:'pointer', color:C.ink, font:'inherit' },
        thumb:  { width:'100%', aspectRatio:'1 / 1', objectFit:'cover', display:'block', background:C.panel },
        meta:   { padding:'6px 8px 8px', fontSize:11, lineHeight:1.45 },
    };

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.header}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <button onClick={onBack} style={{ background:'none', border:'none', color:'#fff', fontSize:'22px', cursor:'pointer', padding:0, lineHeight:1 }}>←</button>
                    <div>
                        <div style={{ fontSize:'10px', color:C.red, letterSpacing:'2px', textTransform:'uppercase', fontWeight:800 }}>Soulflower</div>
                        <div style={{ fontSize:'19px', fontWeight:800, color:'#fff' }}>Packed Box Images</div>
                    </div>
                </div>
            </div>

            <div style={S.body}>
                <form onSubmit={runSearch} style={{ marginBottom:18 }}>
                    <div style={S.label}>PO Number</div>
                    <div style={{ display:'flex', gap:8, alignItems:'stretch' }}>
                        <input
                            type="search"
                            enterKeyHint="search"
                            placeholder="e.g. BLK_MP_204596199"
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setError(''); }}
                            style={S.input}
                            autoComplete="off"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                        <button type="submit" disabled={loading || !query.trim()} style={{ ...S.btn, background: loading || !query.trim() ? '#bbb' : C.ink }}>
                            {loading ? '…' : 'Search'}
                        </button>
                    </div>
                </form>

                {error && (
                    <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'10px 12px', color:'#dc2626', fontSize:13, marginBottom:16 }}>
                        {error}
                    </div>
                )}

                {loading && (
                    <div style={{ textAlign:'center', color:C.gray, fontSize:13, padding:'30px 0' }}>Searching photos…</div>
                )}

                {!loading && searched && (
                    <>
                        {soMatches.length > 0 && (
                            <div style={{ marginBottom:14, display:'flex', flexDirection:'column', gap:6 }}>
                                {soMatches.map(so => (
                                    <div key={so.voucher_no} style={{ background:C.panel, border:`1px solid ${C.line}`, borderRadius:8, padding:'8px 10px', fontSize:12, lineHeight:1.4 }}>
                                        <b>{so.buyer || so.consignee || '—'}</b>
                                        <div style={{ color:C.gray }}>PO {so.order_ref_no || '—'} · SO {so.voucher_no} · {formatDate(so.so_date)}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ ...S.label, marginBottom:10 }}>
                            {images.length} photo{images.length === 1 ? '' : 's'} for "{searched}"
                        </div>

                        {images.length === 0 ? (
                            <div style={{ padding:'28px 16px', textAlign:'center', color:C.gray, fontSize:13, background:C.panel, border:`1px solid ${C.line}`, borderRadius:10 }}>
                                No box photos found for this PO number.<br />Check the number and try again.
                            </div>
                        ) : (
                            <div style={S.grid}>
                                {images.map((img, i) => (
                                    <button key={img.id} type="button" style={S.tile} onClick={() => setViewIdx(i)}>
                                        <img src={img.view_url} alt={img.po_number} loading="lazy" style={S.thumb} />
                                        <div style={S.meta}>
                                            <div style={{ fontWeight:700, fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{img.po_number}</div>
                                            <div style={{ color:C.gray }}>{formatDate(img.uploaded_at)}{img.uploaded_by ? ` · ${img.uploaded_by}` : ''}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {viewIdx !== null && images[viewIdx] && (
                <PhotoViewer
                    images={images}
                    index={viewIdx}
                    onIndex={setViewIdx}
                    onClose={() => setViewIdx(null)}
                />
            )}
        </div>
    );
}

// ── Full-screen viewer ─────────────────────────────────────────────────────
// One finger: swipe left/right to change photo (or pan when zoomed).
// Two fingers: pinch zoom. Double-tap: zoom in at that point / back out.
// The phone's Back button closes the viewer instead of leaving the page.

const MAX_SCALE = 5;
const SWIPE_PX  = 60;

function PhotoViewer({ images, index, onIndex, onClose }) {
    const [view, setView]   = useState({ s: 1, x: 0, y: 0 });
    const [dragX, setDragX] = useState(0);       // live swipe offset while not zoomed
    const [animate, setAnimate] = useState(false);
    const g = useRef({});                        // gesture state, not rendered
    const lastTap = useRef(0);
    const lastTouch = useRef(0);                 // to ignore the browser's emulated dblclick after taps
    const boxRef = useRef(null);
    const img = images[index];
    const total = images.length;

    const reset = () => { setView({ s: 1, x: 0, y: 0 }); setDragX(0); };
    const go = useCallback((dir) => {
        const next = index + dir;
        if (next < 0 || next >= total) return;
        setView({ s: 1, x: 0, y: 0 });
        setDragX(0);
        onIndex(next);
    }, [index, total, onIndex]);

    // Back button closes the viewer; lock page scroll while open.
    useEffect(() => {
        window.history.pushState({ whBoxViewer: true }, '');
        const onPop = () => onClose();
        window.addEventListener('popstate', onPop);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('popstate', onPop);
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const close = () => {
        if (window.history.state?.whBoxViewer) window.history.back(); // popstate -> onClose
        else onClose();
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [go]);

    // Keep a zoomed image from being dragged fully off-screen.
    function clamp(s, x, y) {
        const r = boxRef.current?.getBoundingClientRect();
        if (!r || s <= 1) return { s: Math.max(1, s), x: 0, y: 0 };
        const mx = (r.width * (s - 1)) / 2;
        const my = (r.height * (s - 1)) / 2;
        return { s, x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) };
    }

    // Point relative to the viewer centre (zoom origin).
    function rel(clientX, clientY) {
        const r = boxRef.current.getBoundingClientRect();
        return { x: clientX - (r.left + r.width / 2), y: clientY - (r.top + r.height / 2) };
    }

    function zoomAt(p, s0, x0, y0, s1) {
        // keep the content point under p fixed while scaling s0 -> s1
        const k = s1 / s0;
        return clamp(s1, p.x - (p.x - x0) * k, p.y - (p.y - y0) * k);
    }

    function onTouchStart(e) {
        lastTouch.current = Date.now();
        setAnimate(false);
        const t = e.touches;
        if (t.length === 2) {
            const d = Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
            g.current = { mode: 'pinch', d0: d, v0: view, mid: rel((t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2) };
            setDragX(0);
        } else if (t.length === 1) {
            g.current = { mode: view.s > 1 ? 'pan' : 'swipe', sx: t[0].clientX, sy: t[0].clientY, v0: view, moved: false };
        }
    }

    function onTouchMove(e) {
        const t = e.touches;
        const st = g.current;
        if (st.mode === 'pinch' && t.length === 2) {
            const d = Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
            const s1 = Math.min(MAX_SCALE, Math.max(1, st.v0.s * (d / st.d0)));
            setView(zoomAt(st.mid, st.v0.s, st.v0.x, st.v0.y, s1));
        } else if (t.length === 1 && (st.mode === 'pan' || st.mode === 'swipe')) {
            const dx = t[0].clientX - st.sx;
            const dy = t[0].clientY - st.sy;
            if (Math.abs(dx) > 6 || Math.abs(dy) > 6) st.moved = true;
            if (st.mode === 'pan') setView(clamp(st.v0.s, st.v0.x + dx, st.v0.y + dy));
            else setDragX(dx);
        }
    }

    function onTouchEnd(e) {
        lastTouch.current = Date.now();
        const st = g.current;
        if (st.mode === 'swipe') {
            setAnimate(true);
            if (dragX <= -SWIPE_PX && index < total - 1) go(1);
            else if (dragX >= SWIPE_PX && index > 0) go(-1);
            else setDragX(0);
            if (!st.moved) handleTap(e.changedTouches[0]);
        } else if (st.mode === 'pan' && !st.moved) {
            handleTap(e.changedTouches[0]);
        }
        if (e.touches.length === 1) {
            // pinch -> one finger left down: continue as a pan from here
            g.current = { mode: view.s > 1 ? 'pan' : 'none', sx: e.touches[0].clientX, sy: e.touches[0].clientY, v0: view, moved: true };
        } else if (e.touches.length === 0) {
            g.current = { mode: 'none' };
        }
    }

    function handleTap(touch) {
        const now = Date.now();
        const last = lastTap.current;
        lastTap.current = now;
        if (now - last < 300) {
            lastTap.current = 0;
            setAnimate(true);
            if (view.s > 1) reset();
            else setView(zoomAt(rel(touch.clientX, touch.clientY), 1, 0, 0, 2.5));
        }
    }

    function onDoubleClick(e) {
        // desktop / mouse equivalent of double-tap
        if (Date.now() - lastTouch.current < 1000) return;
        setAnimate(true);
        if (view.s > 1) reset();
        else setView(zoomAt(rel(e.clientX, e.clientY), 1, 0, 0, 2.5));
    }

    const navBtn = (side) => ({
        position:'absolute', top:'50%', [side]:8, transform:'translateY(-50%)',
        width:40, height:40, borderRadius:'50%', border:'none',
        background:'rgba(255,255,255,0.14)', color:'#fff', fontSize:24, lineHeight:'40px',
        cursor:'pointer', zIndex:3, padding:0,
    });

    return (
        <div style={{ position:'fixed', inset:0, background:'#000', zIndex:1000, display:'flex', flexDirection:'column', color:'#fff', fontFamily:'-apple-system, BlinkMacSystemFont, sans-serif' }}>
            {/* Top bar */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', flexShrink:0 }}>
                <div style={{ fontSize:14, fontWeight:700 }}>{index + 1} / {total}</div>
                <button onClick={close} aria-label="Close" style={{ background:'rgba(255,255,255,0.14)', border:'none', color:'#fff', width:36, height:36, borderRadius:'50%', fontSize:18, cursor:'pointer' }}>✕</button>
            </div>

            {/* Image area */}
            <div
                ref={boxRef}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onTouchCancel={onTouchEnd}
                onDoubleClick={onDoubleClick}
                style={{ flex:1, position:'relative', overflow:'hidden', touchAction:'none', userSelect:'none' }}
            >
                <img
                    key={img.id}
                    src={img.view_url}
                    alt={img.po_number}
                    draggable={false}
                    style={{
                        position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'contain',
                        transform:`translate(${view.x + dragX}px, ${view.y}px) scale(${view.s})`,
                        transition: animate ? 'transform 0.2s ease-out' : 'none',
                        willChange:'transform',
                    }}
                />
                {index > 0 && view.s === 1 && (
                    <button onClick={() => go(-1)} aria-label="Previous" style={navBtn('left')}>‹</button>
                )}
                {index < total - 1 && view.s === 1 && (
                    <button onClick={() => go(1)} aria-label="Next" style={navBtn('right')}>›</button>
                )}
            </div>

            {/* Caption */}
            <div style={{ padding:'10px 16px 18px', flexShrink:0, fontSize:12, lineHeight:1.5, textAlign:'center' }}>
                <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:13 }}>{img.po_number}</div>
                <div style={{ color:'#aaa' }}>
                    {formatDate(img.uploaded_at)}{img.uploaded_by ? ` · ${img.uploaded_by}` : ''}
                    {' · '}pinch or double-tap to zoom
                </div>
            </div>
        </div>
    );
}
